const SCENE_UPDATE_EVENT = 'updateSceneCalled';

/** One running animation per key. Starting a new one on the same key replaces the previous one. */
const runningAnimations = new Map<string, () => void>();

export interface FrameAnimationOptions {
  key: string;
  durationSeconds: number;
  /** Called once per rendered frame with a progress between 0 and 1. */
  onProgress: (progress: number) => void;
  /** Called only when the animation reached its end, never when it was interrupted. */
  onComplete?: () => void;
}

/** Ends the animation registered under `key` without jumping to its target value. */
export function stopFrameAnimation(key: string): void {
  const stop = runningAnimations.get(key);
  if (stop) stop();
}

/**
 * Drives `onProgress` once per rendered frame until `durationSeconds` have elapsed.
 * Resolves `true` when the animation reached its end and `false` when it was interrupted.
 *
 * An interrupted animation resolves instead of staying pending forever: otherwise the
 * awaiting action node never fires its "Bei Ende" exec pin and every node behind it is
 * skipped silently.
 *
 * A duration of zero or a non-finite duration (speed 0) finishes immediately instead of
 * never ending.
 */
export function runFrameAnimation({
  key,
  durationSeconds,
  onProgress,
  onComplete
}: FrameAnimationOptions): Promise<boolean> {
  stopFrameAnimation(key);

  if (!Number.isFinite(durationSeconds) || durationSeconds <= 0) {
    onProgress(1);
    onComplete?.();
    return Promise.resolve(true);
  }

  return new Promise((resolve) => {
    let elapsedSeconds = 0;
    let lastTimestamp = performance.now();

    const stop = (hasCompleted: boolean) => {
      window.removeEventListener(SCENE_UPDATE_EVENT, onFrame);
      runningAnimations.delete(key);
      resolve(hasCompleted);
    };

    const onFrame = () => {
      const now = performance.now();
      elapsedSeconds += (now - lastTimestamp) / 1000;
      lastTimestamp = now;

      const progress = Math.min(elapsedSeconds / durationSeconds, 1);
      onProgress(progress);
      if (progress < 1) return;

      onComplete?.();
      stop(true);
    };

    runningAnimations.set(key, () => stop(false));
    window.addEventListener(SCENE_UPDATE_EVENT, onFrame);
  });
}
